import { useState, type FormEvent } from 'react'
import { api, ApiRequestError } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import './Login.css'

/**
 * Panel sign-in.
 *
 * Uses the app's own six-digit e-mail code flow (screens 9a / 4u / 5d / 6c)
 * rather than a separate password login, so there is one account per person and
 * one place where the three-attempt lock is enforced.
 */
export function Login() {
  const { signIn } = useAuth()

  const [step, setStep] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [devCode, setDevCode] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function requestCode(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)

    try {
      const response = await api.auth.sendCode(email.trim())
      // Present only while the backend's mailer is in log-only mode.
      setDevCode(response.devCode ?? null)
      setStep('code')
    } catch (cause) {
      handle(cause)
    } finally {
      setBusy(false)
    }
  }

  async function submitCode(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)

    try {
      await signIn(email.trim(), code.trim())
    } catch (cause) {
      handle(cause)
      // A wrong code stays on this step so the remaining-attempts hint is
      // visible; the lock message replaces it on the third failure.
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  function handle(cause: unknown) {
    if (cause instanceof ApiRequestError) {
      // The server's copy already names the remaining attempts, so the
      // attemptsLeft detail is not repeated here.
      setError(cause.message)
    } else if (cause instanceof Error) {
      setError(cause.message)
    } else {
      setError('Gözlənilməz xəta baş verdi.')
    }
  }

  return (
    <div className="login">
      <form className="login__card" onSubmit={step === 'email' ? requestCode : submitCode}>
        <div className="login__brand">
          <span className="login__mark" aria-hidden="true">
            SB
          </span>
          <div>
            <div className="overline">Səbəb yoxsa Bəhanə</div>
            <h2>Moderasiya paneli</h2>
          </div>
        </div>

        {step === 'email' ? (
          <>
            <label className="login__label" htmlFor="email">
              E-poçt ünvanı
            </label>
            <input
              id="email"
              className="input"
              type="email"
              autoComplete="email"
              required
              autoFocus
              placeholder="ad@nümunə.az"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p className="muted login__hint">
              Ünvanına 6 rəqəmli kod göndəriləcək.
            </p>
          </>
        ) : (
          <>
            <label className="login__label" htmlFor="code">
              {email} ünvanına göndərilən kod
            </label>
            <input
              id="code"
              className="input login__code"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              autoComplete="one-time-code"
              required
              autoFocus
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
            {devCode && (
              <p className="login__devcode">
                Test rejimi — kod: <span className="numeral">{devCode}</span>
              </p>
            )}
            <button
              type="button"
              className="login__link"
              onClick={() => {
                setStep('email')
                setCode('')
                setError(null)
              }}
            >
              E-poçtu dəyiş
            </button>
          </>
        )}

        {error && (
          <p className="login__error" role="alert">
            {error}
          </p>
        )}

        <button className="btn btn--primary login__submit" type="submit" disabled={busy}>
          {busy ? 'Gözlə…' : step === 'email' ? 'Kod göndər' : 'Daxil ol'}
        </button>
      </form>
    </div>
  )
}
